import { useState } from 'react';
import type { ArtworkApiError } from '../services/apiClient';
import { ConfirmAlert } from '../ui/confirm';
import { IconButton } from '../ui/controls';
import type { MenuEntry } from '../ui/menu';
import { archiveFile, deleteFileForever, listArchived, listTrashed, restoreFile } from './api';
import { FileCard, FileGrid, PlaceholderGrid } from './FileCard';
import { ErrorLine, Nothing, ShowMore, Title } from './parts';
import { PhoneEmptyState, usePhone } from './PhoneEmptyState';
import { timeLeft, type ProjectInfo, type PutAwayRow } from './rows';
import { useFileList } from './useFileList';

export type PutAwayKind = 'trash' | 'archived';

/**
 * The Trash (`/?view=trash`) and Archived (`/?view=archived`), drawn as All Files is: the 72 px
 * header, then my files as cards. Both are the maker's own (artwork-back answers only those).
 *
 * A file in the Trash cannot be opened, so a press on its card opens its menu: Restore, or Delete
 * Forever after one question. Each card says how long it has left; at 30 days artwork-back deletes
 * it for good by itself. An archived file still opens; Unarchive puts it back in every list.
 */
export function PutAwayPage({
  kind,
  org,
  onBack,
  onOpen,
  projectOf,
  onChanged,
}: {
  kind: PutAwayKind;
  org: string;
  onBack: () => void;
  onOpen: (file: PutAwayRow) => void;
  projectOf: (projectId: string | null) => ProjectInfo | null;
  /** A file went back into the lists, or was deleted for good: they are read again. */
  onChanged: (file: PutAwayRow) => void;
}) {
  const trash = kind === 'trash';
  const list = useFileList<PutAwayRow>({}, `${org}|${kind}`, undefined, { read: trash ? listTrashed : listArchived });
  const [failure, setFailure] = useState<string | null>(null);
  /** A phone draws an empty or failed list as the suite's (pick E); a laptop keeps these lines. */
  const phone = usePhone();
  const [doomed, setDoomed] = useState<PutAwayRow | null>(null);

  const act = async (file: PutAwayRow, call: () => Promise<void>) => {
    setFailure(null);
    try {
      await call();
      list.patch(file.id, null);
      onChanged(file);
    } catch (err) {
      setFailure((err as ArtworkApiError).message);
    }
  };

  const menu = (file: PutAwayRow) => (): MenuEntry[] =>
    trash
      ? [
          { id: 'restore', label: 'Restore', onSelect: () => void act(file, () => restoreFile(file.id)) },
          { kind: 'separator' },
          { id: 'forever', label: 'Delete Forever…', icon: 'trash', danger: true, onSelect: () => setDoomed(file) },
        ]
      : [
          { id: 'open', label: 'Open', onSelect: () => onOpen(file) },
          { id: 'tab', label: 'Open in New Tab', onSelect: () => window.open(`/f/${file.id}`, '_blank', 'noopener') },
          { kind: 'separator' },
          { id: 'unarchive', label: 'Unarchive', onSelect: () => void act(file, () => archiveFile(file.id, false)) },
        ];

  const error = failure ?? list.error;
  return (
    <>
      <header className="mb-[8px] flex h-[72px] items-center gap-[12px]">
        <IconButton icon="back" label="Back" size={30} onClick={onBack} />
        <div className="min-w-0 flex-1">
          <Title count={list.rows ? `${list.rows.length}${list.more ? '+' : ''}` : undefined}>{trash ? 'Trash' : 'Archived'}</Title>
        </div>
      </header>
      {trash ? <p className="mb-[16px] text-[13px] text-label-2">Files in Trash are deleted for good 30 days after they were moved there.</p> : null}
      {error && !(phone && !failure && list.rows === null) ? <ErrorLine>{error}</ErrorLine> : null}
      {list.rows === null ? (
        list.error ? (
          phone ? <PhoneEmptyState tone="error" title={trash ? 'Couldn’t load Trash' : 'Couldn’t load your archived files'} onRetry={list.reload} /> : null
        ) : (
          <PlaceholderGrid />
        )
      ) : list.rows.length === 0 && phone ? (
        trash ? (
          <PhoneEmptyState title="Trash is empty" line={null} />
        ) : (
          <PhoneEmptyState title="Nothing archived" line="Files you archive wait here, out of your lists." />
        )
      ) : list.rows.length === 0 ? (
        <Nothing>{trash ? 'Trash is empty.' : 'No archived files.'}</Nothing>
      ) : (
        <FileGrid>
          {list.rows.map((file) => (
            <li key={file.id}>
              <FileCard
                file={file}
                project={projectOf(file.projectId)}
                when={(trash ? file.deletedAt : file.archivedAt) ?? file.updatedAt}
                line={trash ? timeLeft(file.purgeAt) : undefined}
                onOpen={trash ? undefined : () => onOpen(file)}
                menu={menu(file)}
              />
            </li>
          ))}
        </FileGrid>
      )}
      <ShowMore list={list} />
      {doomed ? (
        <ConfirmAlert
          title={`Delete “${doomed.name}” forever?`}
          message="It is deleted for good, for everyone. This can’t be undone."
          action="Delete Forever"
          onConfirm={() => {
            const file = doomed;
            setDoomed(null);
            void act(file, () => deleteFileForever(file.id));
          }}
          onCancel={() => setDoomed(null)}
        />
      ) : null}
    </>
  );
}
